import React, { useCallback, useEffect, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { useAutoApplyAvailability, useAutoApplyState } from '../../controllers/auto-apply';

/**
 * Props for AutoApplyToggleMenuItem
 */
interface AutoApplyToggleMenuItemProps {
	/** Session data for backend persistence */
	sessionData?: { cloudId: string; userId: string | null };

	/** Content context (e.g., 'global', 'staging-area') */
	contentContext?: ContentContext;

	/** Whether to use current page context */
	useCurrentPageContext?: boolean;

	/** Whether the conversation is agentic */
	isConversationAgentic?: boolean;

	/** Product key to determine availability logic (e.g., 'jira', 'confluence') */
	productKey?: 'jira' | 'confluence' | string;

	/**
	 * Required: Custom component to render the toggle item.
	 * This allows consumers to control all formatting and styling.
	 */
	renderToggleItem: (props: {
		toggleId: string;
		icon: React.ReactNode;
		title: React.ReactNode;
		subtitle?: React.ReactNode;
		onToggle: (active: boolean) => void;
		isActive: boolean;
		isDisabled: boolean;
	}) => React.ReactNode;

	/** Custom icon renderer. Receives selected state and ref for animations. */
	renderIcon?: (props: {
		selected: boolean;
		ref: React.Ref<{ runAnimation: () => void }>;
	}) => React.ReactNode;

	/** Custom title (overrides default localized message) */
	title?: React.ReactNode;

	/** Custom subtitle (overrides default localized message) */
	subtitle?: React.ReactNode;

	/** Callback fired when toggle state changes successfully */
	onToggleChange?: (enabled: boolean) => void;

	/** Callback fired when toggle fails to update */
	onError?: (error: Error) => void;
}

/**
 * AutoApplyToggleMenuItem - Headless component for auto-apply toggle
 *
 * This component provides state management and business logic for the auto-apply toggle.
 * Formatting is provided via the `renderToggleItem` prop.
 */
export const AutoApplyToggleMenuItem = ({
	sessionData,
	contentContext,
	useCurrentPageContext,
	isConversationAgentic,
	productKey,
	renderToggleItem,
	renderIcon,
	title: customTitle,
	subtitle: customSubtitle,
	onToggleChange,
	onError,
}: AutoApplyToggleMenuItemProps): React.ReactElement | null => {
	// ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
	const { sendAnalyticsEvent } = useAnalytics();

	// Check availability first
	const { autoApplyAvailability } = useAutoApplyAvailability(
		contentContext!,
		useCurrentPageContext!,
		isConversationAgentic!,
		productKey,
	);

	// Check if feature is enabled and available
	const isAvailable =
		fg('enable_auto_apply_toggling') && autoApplyAvailability.type === 'available';

	// Only fetch state if available (pass undefined sessionData to prevent backend calls)
	const { enabled, loading, setEnabled } = useAutoApplyState(
		isAvailable ? sessionData : undefined,
		contentContext,
		useCurrentPageContext,
		isConversationAgentic,
	);

	// Create ref for icon animation
	const iconContainerRef = useRef<{ runAnimation: () => void }>(null);

	// Track the visual state locally to prevent optimistic UI updates
	const [visualState, setVisualState] = useState(enabled);

	// Sync visual state with actual enabled state
	useEffect(() => {
		setVisualState(enabled);
	}, [enabled]);

	const handleToggle = useCallback(
		async (newEnabled: boolean): Promise<void> => {
			// Don't allow toggling if already in progress
			if (loading) {
				return;
			}

			// Capture current state before attempting update
			const previousState = enabled;

			// Trigger icon animation when enabling
			if (newEnabled) {
				iconContainerRef.current?.runAnimation();
			}

			try {
				// Use the hook's setEnabled method which handles backend + localStorage
				await setEnabled(newEnabled);

				// Send analytics event
				sendAnalyticsEvent({
					action: 'change',
					actionSubject: 'toggle',
					actionSubjectId: 'autoApplyToggle',
					attributes: {
						isSelected: newEnabled,
					},
				});

				// Call custom callback if provided
				onToggleChange?.(newEnabled);
			} catch (err) {
				const error = err instanceof Error ? err : new Error('Unknown error');

				// Send analytics event for error
				sendAnalyticsEvent({
					action: 'failed',
					actionSubject: 'toggle',
					actionSubjectId: 'autoApplyToggle',
					attributes: {
						isSelected: newEnabled,
						errorMessage: error.message,
					},
				});

				// Ensure visual state reverts to previous state on error
				setVisualState(previousState);

				// Call error callback so parent can handle UI
				onError?.(error);
			}
		},
		[setEnabled, sendAnalyticsEvent, onToggleChange, onError, enabled, loading],
	);

	// Don't render if feature gate is disabled
	if (!fg('enable_auto_apply_toggling')) {
		return null;
	}

	// Don't render if auto-apply is not available
	if (autoApplyAvailability.type === 'unavailable') {
		return null;
	}

	// Prepare title and subtitle (use custom or default to empty)
	const title = customTitle ?? 'Auto-apply AI changes';
	const subtitle = customSubtitle;

	// Prepare icon (use custom renderer or no icon)
	const icon = renderIcon ? renderIcon({ selected: visualState, ref: iconContainerRef }) : null;

	// Render using custom renderer
	return (
		<>
			{renderToggleItem({
				toggleId: 'auto_apply_toggle',
				icon,
				title,
				subtitle,
				onToggle: handleToggle,
				isActive: visualState,
				isDisabled: loading,
			})}
		</>
	);
};
